import {Component, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {DialogService} from "../../../shared/services/dialog.service";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {UploaderModule} from "@syncfusion/ej2-angular-inputs";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {DocumentService} from "../../../shared/services/document.service";
import {LanguageService} from "../../../shared/services/language.service";
import {ToasterService} from "../../../shared/services/toaster.service";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {DialogDocumentsCommanderComponent} from "../dialog-documents-commander/dialog-documents-commander.component";
import {ItemDocumentsPreviewSelectedComponent} from "../item-documents-preview-selected/item-documents-preview-selected.component";
import {SettingsMenuItemType} from '../../../shared/types/generated/settingsMenuItemType';
import {Router} from '@angular/router';
import {map, Observable, take} from "rxjs";
import {FileStorageResource} from '../../../shared/types/generated/fileStorageResource';
import {DocumentsRemoveFileHook, DocumentsUploadFilesHook} from '../documents-upload-hooks.type';

@Component({
    selector: 'app-dialog-documents-upload',
    imports: [
        DialogModule,
        FormButtonComponent,
        HeadlineComponent,
        TooltipModule,
        TranslatePipe,
        UploaderModule,
        TooltipComponent,
        IconBadgeComponent,
        ContentComponent,
        DialogDocumentsCommanderComponent,
        ItemDocumentsPreviewSelectedComponent,
        FormLabelComponent
    ],
    templateUrl: './dialog-documents-upload.component.html',
    styleUrl: './dialog-documents-upload.component.scss'
})
export class DialogDocumentsUploadComponent implements OnInit {
  public dialogService: DialogService = inject(DialogService);
  public documentService: DocumentService = inject(DocumentService);
  private toasterService: ToasterService = inject(ToasterService);
  private languageService: LanguageService = inject(LanguageService);
  private router: Router = inject(Router);

  @Input() public visible: boolean = false;
  @Input() public title: string = '';
  @Input() public tooltip: string = '';
  @Input() public description: string = '';
  @Input() public multiple: boolean = false;
  @Input() public allowExtensions: string[] = [];
  @Input() public view: 'dialog' | 'component' = 'dialog';
  @Input() public type: 'image' | 'media' | 'attachment' | 'both' = 'both';
  @Input() public requireFiles: boolean = false;
  @Input() public files: FileStorageResource[] = [];
  @Input() public hasError: boolean = false;
  @Input() public disabled: boolean = false;
  @Input() public templateModule: SettingsMenuItemType | '' = '';
  @Input() public hideUploadedContent: boolean = false;
  @Input() public hideMyFilesOption: boolean = false;
  @Input() public directory?: string;
  @Input() public uploadFilesFn?: DocumentsUploadFilesHook;
  @Input() public removeFileFn?: DocumentsRemoveFileHook;
  @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public documentsUploaded: EventEmitter<FileStorageResource[]> = new EventEmitter<FileStorageResource[]>();
  @Output() public filesChange: EventEmitter<FileStorageResource[]> = new EventEmitter<FileStorageResource[]>();

  public documentsCommanderDialog: boolean = false;
  private allowedMediaTypes: string[] = [];
  private forbiddenTypes: string[] = [];
  public isDraggingOver: boolean = false;
  public resolvedTemplateModule: SettingsMenuItemType | '' = '';
  public canOpenDocumentsCommander: boolean = true;
  public disabledUploadMessage: string = '';
  public disabledReadOnlyHint: string = '';

  public ngOnInit(): void {
    this.disabledUploadMessage = this.languageService.getTranslationByKey('DOCUMENTSUPLOADSDISABLED');
    this.disabledReadOnlyHint = this.languageService.getTranslationByKey('DOCUMENTSREADONLYHINT');
    this.resolvedTemplateModule = this.templateModule || this.mapRouteToModule(this.router.url);
    this.canOpenDocumentsCommander = this.resolvedTemplateModule !== SettingsMenuItemType.CHAT
      && this.resolvedTemplateModule !== SettingsMenuItemType.PROFILE;
    if(this.type === 'image') {
      this.allowedMediaTypes = this.documentService.allowedImageTypes;
      this.forbiddenTypes = [...this.documentService.allowedVideoTypes, ...this.documentService.allowedAudioTypes];
    } else {
      this.allowedMediaTypes = this.type === 'media' ? this.documentService.allowedMediaTypes : [];
      this.forbiddenTypes = this.type === 'attachment' ? this.documentService.allowedMediaTypes : [];
    }
  }

  public close(): void {
    this.visibleChange.emit(false);
  }

  public save(): void {
    this.documentsUploaded.emit(this.files);
    this.close();
  }

  private uploadDocuments(documents: FileList): void {
    if (this.disabled) {
      return;
    }

    if (documents.length === 0) {
      this.toasterService.error(this.languageService.getTranslationByKey('DOCUMENTS'), this.languageService.getTranslationByKey('DOCUMENTSNOFILES'));
      return;
    }
    if (documents.length > 1 && !this.multiple) {
      this.toasterService.error(this.languageService.getTranslationByKey('DOCUMENTS'), this.languageService.getTranslationByKey('DOCUMENTSTOOMANYFILES'));
      return;
    }
    if (this.allowedMediaTypes.length > 0 && this.checkType(documents, this.allowedMediaTypes)) {
      this.toasterService.error(this.languageService.getTranslationByKey('DOCUMENTS'), this.languageService.getTranslationByKey('DOCUMENTSINVALIDTYPE'));
      return;
    }
    if (this.forbiddenTypes.length > 0 && !this.checkType(documents, this.forbiddenTypes)) {
      this.toasterService.error(this.languageService.getTranslationByKey('DOCUMENTS'), this.languageService.getTranslationByKey('DOCUMENTSINVALIDTYPE'));
      return;
    }
    this.resolveUploadRequest(documents)
      .pipe(take(1))
      .subscribe({
        next: (nextFiles) => {
          this.files = nextFiles;
          this.filesChange.emit(this.files);
        },
        error: (err) => {
          console.error(err);
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fileSelect(event: any): void {
    if (this.disabled) {
      return;
    }

    const documents = event.target.files as FileList | undefined;
    if (documents !== undefined) {
      this.uploadDocuments(documents);
    }
  }

  public fileDrop(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled) {
      this.isDraggingOver = false;
      const documents = event.dataTransfer?.files as FileList | undefined;
      if (documents !== undefined) {
        this.uploadDocuments(documents);
      }
    }
  }

  public dragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled) {
      this.isDraggingOver = true;
    }
  }

  public onDragLeave(): void {
    if (!this.disabled) {
      this.isDraggingOver = false;
    }
  }



  public update(files: FileStorageResource[] = this.files): void {
    this.files = files;
    this.filesChange.emit(this.files);
  }

  private resolveUploadRequest(documents: FileList): Observable<FileStorageResource[]> {
    if (this.uploadFilesFn !== undefined) {
      return this.uploadFilesFn(documents, [...this.files]);
    }

    return this.documentService.uploadFiles(
      documents,
      this.directory !== 'public' ? this.directory : undefined,
      this.directory === 'public' ? true : undefined
    ).pipe(
      take(1),
      map((responseFiles) => this.multiple ? [...responseFiles, ...this.files] : responseFiles)
    );
  }

  private checkType(documents: FileList, allowedType: string[]): boolean {
    let foundError = false;
    Array.from(documents).forEach((document) => {
      if (!allowedType.includes(document.name.toLowerCase().split('.').pop()!)) {
        foundError = true;
      }
    });
    return foundError;
  }

  public openDocumentsCommander(): void {
    if (this.disabled || !this.canOpenDocumentsCommander) {
      return;
    }
    this.documentsCommanderDialog = true;
  }

  private mapRouteToModule(url: string): SettingsMenuItemType | '' {
    const route = (url.split('?')[0].split('/')[1] || '').toLowerCase();
    const byRoute: Record<string, SettingsMenuItemType> = {
      chat: SettingsMenuItemType.CHAT,
      calendar: SettingsMenuItemType.CALENDAR,
      contacts: SettingsMenuItemType.CONTACTS,
      community: SettingsMenuItemType.COMMUNITIES,
      feedback: SettingsMenuItemType.FEEDBACK,
      knowledgebase: SettingsMenuItemType.KNOWLEDGEBASE,
      news: SettingsMenuItemType.NEWS,
      meetings: SettingsMenuItemType.MEETINGS,
      profile: SettingsMenuItemType.PROFILE,
      settings: SettingsMenuItemType.SETTINGS,
      tv: SettingsMenuItemType.TV,
      workflow: SettingsMenuItemType.WORKFLOW
    };

    return byRoute[route] ?? '';
  }
}
